import { Box, styled } from '@mui/material';

// eslint-disable-next-line import-x/no-internal-modules
import { LEGEND_SPEEDS, UNIT_LABELS } from '../lib/windConversions';
// eslint-disable-next-line import-x/no-internal-modules
import { themeColorAtSpeed } from '../lib/windGl';
import { useWindTheme } from '../model';

// eslint-disable-next-line import-x/no-internal-modules
import type { WindColorTheme, WindSpeedUnit } from '../model/types';

const PREFIX = 'TSMED-WindSpeedLegend';
const legendClasses = {
  toggleLegend: `${PREFIX}-toggleLegend`,
  label: `${PREFIX}-label`,
};

interface WindSpeedLegendProps {
  theme?: WindColorTheme;
  unit?: WindSpeedUnit;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  let r: number;
  let g: number;
  let b: number;
  if (h.length <= 4) {
    r = Number.parseInt(h[0] + h[0], 16);
    g = Number.parseInt(h[1] + h[1], 16);
    b = Number.parseInt(h[2] + h[2], 16);
  } else {
    r = Number.parseInt(h.slice(0, 2), 16);
    g = Number.parseInt(h.slice(2, 4), 16);
    b = Number.parseInt(h.slice(4, 6), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
};

const FADE = 0.07;

// ── Label-aligned gradient positioning ────────────────────────────────────────
// The legend uses space-between flex with 8 items (unit + 7 speed labels).
// Gradient stops must land at the same positions as the label centers so that
// the color under each label matches its speed value.
const LEGEND_WIDTH = 300;
const LEGEND_PADDING = 16; // spacing(2)
const MS_SPEEDS = LEGEND_SPEEDS.ms; // [0, 3, 5, 10, 15, 20, 30]
const TOTAL_ITEMS = MS_SPEEDS.length + 1; // +1 for unit label at index 0
const PAD_PCT = (LEGEND_PADDING / LEGEND_WIDTH) * 100;
const GAP_PCT = (100 - 2 * PAD_PCT) / (TOTAL_ITEMS - 1);
const MAX_LABEL_SPEED = MS_SPEEDS.at(-1); // 30

/** Percent position of the i-th speed label (0-based within MS_SPEEDS). */
const labelPct = (i: number) => PAD_PCT + (i + 1) * GAP_PCT;

/** Map wind speed (m/s) → legend CSS percent, interpolating between labels. */
const speedToPct = (speed: number): number => {
  if (speed <= MS_SPEEDS[0]) {
    return labelPct(0);
  }
  const last = MS_SPEEDS.length - 1;
  if (speed >= MS_SPEEDS[last]) {
    return labelPct(last);
  }
  for (let j = 0; j < last; j++) {
    if (speed <= MS_SPEEDS[j + 1]) {
      const t = (speed - MS_SPEEDS[j]) / (MS_SPEEDS[j + 1] - MS_SPEEDS[j]);
      return labelPct(j) + t * (labelPct(j + 1) - labelPct(j));
    }
  }
  return labelPct(last);
};

const buildLegendGradient = (
  theme: WindColorTheme,
  opacity: number,
  threshold: number,
): string => {
  // Collect theme gradient stops within [0, MAX_LABEL_SPEED]
  const stops: [number, string][] = [];
  for (const [speed, color] of theme.gradient) {
    if (MAX_LABEL_SPEED && speed > MAX_LABEL_SPEED) {
      break;
    }
    stops.push([speed, color]);
  }
  // Synthesize a final stop at MAX_LABEL_SPEED if the last theme stop is below it
  if (
    MAX_LABEL_SPEED &&
    (stops.length === 0 || stops[stops.length - 1][0] < MAX_LABEL_SPEED)
  ) {
    const [r, g, b] = themeColorAtSpeed(MAX_LABEL_SPEED, theme);
    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    stops.push([MAX_LABEL_SPEED, hex]);
  }

  const toRgba = (speed: number, color: string) => {
    const t = MAX_LABEL_SPEED ? speed / MAX_LABEL_SPEED : 0;
    let fade = 1;
    if (threshold > 0) {
      const lo = Math.max(0, threshold - FADE / 2);
      const hi = threshold + FADE / 2;
      if (t <= lo) {
        fade = 0;
      } else if (t < hi) {
        const x = (t - lo) / (hi - lo);
        fade = x * x * (3 - 2 * x);
      }
    }
    return hexToRgba(color, opacity * fade);
  };

  const cssStops = stops.map(([speed, color]) => {
    const pct = speedToPct(speed);
    return `${toRgba(speed, color)} ${pct.toFixed(1)}%`;
  });

  // Pad left edge (0% to speed-0 position) with the first stop color
  const padColor = toRgba(stops[0][0], stops[0][1]);
  const zeroPct = speedToPct(0);
  return `linear-gradient(to right, ${padColor} 0%, ${padColor} ${zeroPct.toFixed(1)}%, ${cssStops.join(', ')})`;
};

const Root = styled(Box)<{ gradient: string }>(
  ({ theme: { spacing }, gradient }) => ({
    display: 'flex',
    width: 300,
    lineHeight: '100%',
    paddingInline: spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: spacing(1),
    background: gradient,
    flexShrink: 0,

    [`.${legendClasses.label}`]: {
      color: 'rgba(255, 255, 255, 0.60)',
      fontVariantNumeric: 'slashed-zero',
      textShadow: '0 1px 1px rgba(0, 0, 0, 0.30)',
      fontSize: '14px',
      fontWeight: 500,
      fontStyle: 'normal',
      lineHeight: 'normal',
    },
  }),
);

export const WindSpeedLegend = ({
  theme: themeProp,
  unit = 'ms',
}: WindSpeedLegendProps) => {
  const { theme: contextTheme, opacity, threshold } = useWindTheme();
  const theme = themeProp ?? contextTheme;

  const gradient = buildLegendGradient(theme, opacity, threshold);

  const speeds = LEGEND_SPEEDS[unit];
  const unitLabel = UNIT_LABELS[unit];

  return (
    <Root gradient={gradient}>
      {[unitLabel, ...speeds].map((text, index) => (
        <Box key={index} className={legendClasses.label}>
          {text}
        </Box>
      ))}
    </Root>
  );
};
